import { PoseTracker } from '../pose/PoseTracker.js';
import { GestureDetector } from '../pose/Gestures.js';
import { SkeletonView } from '../pose/SkeletonView.js';

/**
 * Control por cámara dentro del juego: cámara + detección de pose (Web Worker) + gestos.
 * Cada gesto llega a onGesture(tipo, latenciaMs) en cuanto se detecta, fuera del bucle de render:
 *   'jump' | 'crouch' | 'left' | 'right'   gestos (brazo izquierdo/derecho = left/right)
 *   'lost' | 'found'                        sales de cámara / vuelves
 *   'calibrated'                            calibración terminada
 * La imagen de la cámara no sale del dispositivo: solo se dibuja en los canvas que se pidan.
 */
export class CameraControl {
  constructor({ video, onGesture }) {
    this.video = video;
    this.onGesture = onGesture;
    this.tracker = null;
    this.detector = new GestureDetector();
    this.views = new Map(); // canvas → SkeletonView
    this.status = 'off'; // 'off' | 'loading' | 'on' | 'error'
    this.error = '';
    this.hasPose = false;
    this.latencies = []; // captura → gesto de los últimos gestos (ms)
    this.lastGesture = null;
  }

  get running() {
    return this.status === 'on';
  }

  /** Lista para jugar: cámara encendida y calibrada. */
  get ready() {
    return this.running && this.detector.calibrated;
  }

  /**
   * Enciende la cámara, carga el modelo y empieza a calibrar. Con un perfil de gestos guardado solo
   * hace falta quedarse quieto; sin él, después se calibran tus gestos.
   * Lanza un Error con mensaje en español.
   */
  async start({ profile = null } = {}) {
    if (this.status === 'loading' || this.status === 'on') return;
    this.status = 'loading';
    this.error = '';
    const params = new URLSearchParams(location.search);
    this.tracker = new PoseTracker({
      model: params.get('model') ?? 'lightning',
      backend: params.get('backend') ?? 'auto',
      useWorker: params.get('worker') !== '0',
    });
    this.tracker.onPose = (r) => this.#onPose(r);
    this.tracker.onError = (e) => console.warn('[cámara]', e.message);
    this.detector = new GestureDetector(this.detector.settings, profile);
    this.detector.startCalibration({ train: !profile });
    try {
      await this.tracker.start(this.video);
      this.status = 'on';
    } catch (e) {
      this.tracker.stop();
      this.tracker = null;
      this.status = 'error';
      this.error = e.message;
      throw e;
    }
  }

  stop() {
    this.tracker?.stop();
    this.tracker = null;
    this.status = 'off';
    this.hasPose = false;
  }

  /** train: volver a medir también tus gestos (si no, solo la postura de referencia). */
  recalibrate({ train = false } = {}) {
    if (this.running) this.detector.startCalibration({ train });
  }

  skipTraining() {
    this.detector.skipTraining();
  }

  #onPose(r) {
    const d = this.detector;
    d.setBrightness(this.tracker?.brightness ?? 1);
    const events = d.update(r.keypoints, r.t, r.aspect);
    this.hasPose = !!r.keypoints;
    const latency = performance.now() - r.captureTime;
    for (const e of events) {
      if (e === 'jump' || e === 'crouch' || e === 'left' || e === 'right') {
        this.latencies.push(latency);
        if (this.latencies.length > 20) this.latencies.shift();
        this.lastGesture = { type: e, latency };
      }
      this.onGesture(e, latency);
    }
  }

  /** Dibuja la imagen (en espejo) con el esqueleto en el canvas indicado. */
  draw(canvas) {
    let view = this.views.get(canvas);
    if (!view) this.views.set(canvas, (view = new SkeletonView(canvas)));
    const d = this.detector;
    view.draw(this.hasPose ? d.points : null, {
      video: this.video,
      minScore: d.settings.minScore,
      color: !d.visible ? '#ff6b5b' : d.calibrated ? '#7dff9a' : '#7dd8ff',
    });
  }

  /** Proporción de la imagen de la cámara (ancho / alto). */
  get aspect() {
    return this.video.videoWidth ? this.video.videoWidth / this.video.videoHeight : 4 / 3;
  }

  /** Datos para el panel de depuración. */
  get stats() {
    const s = this.tracker?.stats ?? { fps: 0, inferMs: 0, pipelineMs: 0 };
    const l = this.latencies;
    return {
      backend: this.tracker ? `${this.tracker.info.backend} · ${this.tracker.info.where}` : '-',
      fps: s.fps,
      inferMs: s.inferMs,
      pipelineMs: s.pipelineMs,
      gestureMs: l.length ? l.reduce((a, b) => a + b, 0) / l.length : null,
      last: this.lastGesture,
    };
  }
}
